import { bbox } from '@turf/turf';
import type { FeatureCollection } from 'geojson';
import type { AgencySubGeometry } from '~/api/civilsearch';
import type { InitialViewState } from '~/components2/Map/common';

export const SUB_GEO_SOURCE_ID = 'agency-sub-geometries-source';
export const SUB_GEO_FILL_LAYER_ID = 'agency-sub-geometries-fill';

/** Continental-US fallback view, used when there's no geometry to fit the map to. */
export const DEFAULT_MAP_VIEW: InitialViewState = { longitude: -98.5795, latitude: 39.8283, zoom: 3 };

/**
 * Fit-to-bounds initial view over the union of the given feature collections, or
 * {@link DEFAULT_MAP_VIEW} when there's nothing to fit to or the computed bounds are invalid.
 * Logs when otherwise-present features produce unusable bounds, so bad geometries surface in
 * the console instead of silently falling back to the US view.
 */
export function fitBoundsInitialViewState(
  fcs: (FeatureCollection | undefined)[],
  fitBoundsOptions: mapboxgl.FitBoundsOptions = { padding: 80 },
): InitialViewState {
  const features = fcs.flatMap((fc) => fc?.features ?? []);
  if (features.length === 0) return DEFAULT_MAP_VIEW;
  try {
    const bounds = bbox({ type: 'FeatureCollection', features }) as [number, number, number, number];
    if (!bounds.every(Number.isFinite)) {
      console.warn('fitBoundsInitialViewState: non-finite bounds from features, using default view', bounds);
      return DEFAULT_MAP_VIEW;
    }
    return { bounds, fitBoundsOptions };
  } catch (e) {
    console.warn('fitBoundsInitialViewState: failed to compute bounds, using default view', e);
    return DEFAULT_MAP_VIEW;
  }
}

export function buildSubGeometryFeatureCollection(subGeometries: AgencySubGeometry[]): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: subGeometries.map((sg) => ({
      type: 'Feature',
      properties: { id: sg.id, name: sg.name, status: sg.status },
      geometry: sg.geometry,
    })),
  };
}

/**
 * Human-readable label for a sub-geometry. Falls back to a placeholder when the name is missing
 * OR blank/whitespace-only — a `?? ` check alone would let an empty string `''` render as a blank
 * label.
 */
export function subGeometryDisplayName(name: string | null | undefined): string {
  return name?.trim() ? name : 'Unnamed sub region';
}

/**
 * Whether the current user may create / edit / archive sub regions. The management flag
 * (`agency-edit-sub-regions`) is separate from the view flag (`agency-show-sub-regions`).
 * While the flag is still resolving (`undefined`) we treat the user as NOT allowed, so the
 * management controls never flash and then disappear.
 */
export function canManageSubRegions(managementFlag: boolean | undefined): boolean {
  return managementFlag === true;
}
